import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import copyToClipboard from 'clipboard-copy';
import { SvgIcon, IconName } from '@adjust/dashboard-components';
import { Link as LinkType, TrackEvent } from '../../types';
import { useDropdown } from '../../hooks/useDropdown';
import './Link.scss';

interface LinkProps extends LinkType {
  isHighlighted: boolean;
  isProcessing: boolean;
  isInactive: boolean;
  onTrackEvent: TrackEvent;
  onEdit: (id: number) => void;
  onArchivedToggle: (id: number, isArchived: boolean) => void;
  onRemove: (id: number) => void;
}

export const Link = React.memo<LinkProps>(
  ({
    id,
    title,
    url,
    archived_at,
    isHighlighted,
    isProcessing,
    isInactive,
    isInvalid,
    onTrackEvent,
    onEdit,
    onArchivedToggle,
    onRemove,
  }) => {
    const [isCopied, setIsCopied] = useState(false);
    const dropdown = useDropdown();

    useEffect(() => {
      if (!isCopied) return;
      const timeout = setTimeout(() => setIsCopied(false), 2000);
      return () => clearTimeout(timeout);
    }, [isCopied]);

    const copy = () => {
      copyToClipboard(url);
      onTrackEvent('copy_generated_link');
      setIsCopied(true);
    };

    return (
      <li
        className={classNames('link', {
          'link--copied': isCopied,
          'link--highlighted': isHighlighted,
          'link--inactive': isInactive && !isProcessing,
          'link--actions': dropdown.isOpened,
        })}
        data-testid="link"
      >
        <div className="link__title">{title}:</div>
        <div className="link__content">
          <div className="link__url monospace-text">{url}</div>
          <div className="link__actions">
            <div className="link__dropdown link__dropdown-copied">Copied</div>

            <div className="link__dropdown link__dropdown-actions">
              {!isInvalid && <a onClick={() => onEdit(id)}>Edit link</a>}
              <a onClick={() => onArchivedToggle(id, !!archived_at)}>{archived_at ? 'Unhide link' : 'Hide link'}</a>
              <a onClick={() => onRemove(id)}>Remove link</a>
            </div>

            <button title="Copy to clipboard" className="btn btn--icon" onClick={copy} disabled={isInactive}>
              <SvgIcon name={IconName.Clipboard} />
            </button>

            <button title="Select action" className="btn btn--icon" onClick={dropdown.open} disabled={isInactive}>
              <SvgIcon name={IconName.Cog} />
            </button>
          </div>
        </div>

        {isInvalid && (
          <div className="link__warning">
            <SvgIcon name={IconName.Warning} /> This link is not valid anymore
          </div>
        )}

        {isProcessing && <div className="loader-overlay loader-overlay-show pale" />}
      </li>
    );
  },
);
